import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { Close as CloseIcon, PlayArrow as PlayIcon, Pause as PauseIcon } from "@mui/icons-material";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";

import "../pubvision/AssetsGeneration.css";
import PropTypes from "prop-types";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AudioSummaryDialog = ({
  open,
  onClose,
  audioDuration,
  setAudioDuration,
  audioLanguage,
  setAudioLanguage,
  audioVoice,
  setAudioVoice,
  audioLevel,
  setAudioLevel,
  onStartGeneratingAudio,
}) => {

  const [voices, setVoices] = useState({});
  const [selectedAccent, setSelectedAccent] = useState("");
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchMediaOptions = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${BASE_URL}/get_all_voices`, {
          method: "GET",
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.voiceOvers) {
            setVoices(data.voiceOvers);
            const accentKeys = Object.keys(data.voiceOvers);
            if (accentKeys.length > 0) {
              const defaultAccent = accentKeys[0];
              setSelectedAccent(defaultAccent);
              const accentData = data.voiceOvers[defaultAccent];
              // Auto-select the first voice if none is set or if a legacy gender string
              if (!audioVoice || audioVoice === "Male" || audioVoice === "Female") {
                if (accentData.Male?.length > 0) {
                  setAudioVoice(accentData.Male[0].id);
                } else if (accentData.Female?.length > 0) {
                  setAudioVoice(accentData.Female[0].id);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch voices", error);
      }
    };
    if (open) {
      fetchMediaOptions();
    }
  }, [open]);

  // Reset audio playback state when dialog closes or unmounts
  useEffect(() => {
    if (!open) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingTrackId(null);
      setSelectedAccent("");
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [open]);

  const handleTogglePlay = (e, track) => {
    e.preventDefault();
    e.stopPropagation();

    if (playingTrackId === track.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayingTrackId(null);
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const newAudio = new Audio(track.sample_audio_path || track.url);
      newAudio.play().catch(err => console.error("Failed to play track:", err));
      newAudio.onended = () => setPlayingTrackId(null);
      audioRef.current = newAudio;
      setPlayingTrackId(track.id);
    }
  };

  const levels = [
    "Physicians",
    "Medical Affairs",
    "Clinical Development",
    "Commercial Teams",
    "Market Access & HEOR",
    "Regulatory & Safety",
    "KOLs / Researchers",
    "Executives",
    "Patients"
  ];

  const levelDescriptions = {
    "Physicians": "Frontline clinicians focused on patient care, diagnosis, treatment decisions, and clinical outcomes.",
    "Medical Affairs": "Bridge between clinical data and the medical community — managing publications, advisory boards, and scientific exchange.",
    "Clinical Development": "Designs and runs clinical trials to evaluate safety, efficacy, and dosing of new therapies.",
    "Commercial Teams": "Drive product adoption through sales strategy, marketing, and customer engagement with healthcare providers.",
    "Market Access & HEOR": "Secure payer coverage and demonstrate value through health economics, outcomes research, and reimbursement strategy.",
    "Regulatory & Safety": "Ensure compliance with regulatory requirements, manage drug safety reporting, and oversee pharmacovigilance.",
    "KOLs / Researchers": "Academic leaders and researchers who shape clinical practice through publications, guidelines, and thought leadership.",
    "Executives": "Senior leadership driving corporate strategy, pipeline decisions, and organizational priorities.",
    "Patients": "Individuals receiving medical care, seeking to understand disease states, treatments, and clinical outcomes."
  };

  const audioDurationOptions = [
    { value: "short", label: "Short", desc: "1.5–2 mins" },
    { value: "detailed", label: "Detailed", desc: "4–4.5 mins" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="abstract-video-dialog"
      sx={{
        "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.5)" },
        "& .MuiPaper-root": { fontFamily: "'Inter', sans-serif" },
        "& *": { fontFamily: "'Inter', sans-serif !important" },
      }}
    >
      <DialogTitle className="dialog-title">
        <IconButton
          aria-label="close"
          onClick={onClose}
          className="close-button"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogTitle className="dialog-title main-title">
        <strong>Audio Summary: Enter requirements below</strong>
      </DialogTitle>

      <div className="dialog-subtitle-container">
        <p className="dialog-subtitle">
          Please fill the form below to get the audio summary as per your requirement
        </p>
      </div>

      <DialogContent className="dialog-content">

        <div className="dialog-field">
          <FormControl component="fieldset">
            <FormLabel component="legend" className="dialog-label" focused={false}>
              Choose Audience Level
            </FormLabel>
            <Select
              value={audioLevel}
              onChange={(e) => setAudioLevel(e.target.value)}
              displayEmpty
              className="level-options"
              style={{ marginTop: "12px", width: "100%" }}
            >
              {levels.map((level) => (
                <MenuItem key={level} value={level} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{level}</span>
                  <Tooltip
                    title={levelDescriptions[level]}
                    arrow
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "#fff",
                          color: "#1E4DE3",
                          fontSize: "13px",
                          lineHeight: "1.5",
                          padding: "10px 14px",
                          maxWidth: "350px",
                          border: "1px solid #1E4DE3",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          "& .MuiTooltip-arrow": {
                            color: "#fff",
                            "&::before": { border: "1px solid #1E4DE3" }
                          }
                        }
                      }
                    }}
                  >
                    <IconButton
                      size="small"
                      style={{ padding: "4px", color: "#9ca3af", transition: "all 0.2s ease" }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.1)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                      <AiOutlineInfoCircle size={16} />
                    </IconButton>
                  </Tooltip>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Choose Accent */}
        <div className="dialog-field" style={{ width: "100%", marginTop: "16px" }}>
          <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px" }}>
            Choose Accent
          </FormLabel>
          <FormControl size="small" style={{ width: "100%", marginTop: "4px" }}>
            <Select
              value={selectedAccent}
              onChange={(e) => {
                const newAccent = e.target.value;
                setSelectedAccent(newAccent);
                const accentData = voices[newAccent];
                if (accentData) {
                  if (accentData.Male?.length > 0) {
                    setAudioVoice(accentData.Male[0].id);
                  } else if (accentData.Female?.length > 0) {
                    setAudioVoice(accentData.Female[0].id);
                  } else {
                    setAudioVoice("");
                  }
                }
                if (audioRef.current) {
                  audioRef.current.pause();
                  setPlayingTrackId(null);
                }
              }}
              sx={{
                borderRadius: "8px", fontSize: "13px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E4DE3" }
              }}
            >
              {Object.keys(voices).map(accentKey => (
                <MenuItem key={accentKey} value={accentKey} sx={{ fontSize: "13px" }}>
                  {voices[accentKey]?.display_accent_name || accentKey}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Choose Voiceover */}
        <div className="dialog-field" style={{ width: "100%", marginTop: "16px" }}>
          <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "8px" }}>
            Choose Voiceover
          </FormLabel>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", width: "100%", marginTop: "4px" }}>
            <FormControl size="small" style={{ flex: 1 }}>
              <Select
                value={audioVoice && audioVoice !== "Male" && audioVoice !== "Female" ? audioVoice : ""}
                disabled={!selectedAccent}
                onChange={(e) => {
                  setAudioVoice(e.target.value);
                  if (audioRef.current) {
                    audioRef.current.pause();
                    setPlayingTrackId(null);
                  }
                }}
                sx={{
                  borderRadius: "8px", fontSize: "13px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E4DE3" }
                }}
              >
                {selectedAccent && voices[selectedAccent]?.Male?.length > 0 ? (
                  <ListSubheader sx={{ fontWeight: 'bold', color: '#1E4DE3' }}>Male Voices</ListSubheader>
                ) : null}
                {selectedAccent ? (voices[selectedAccent]?.Male || []).map(voice => (
                  <MenuItem key={voice.id} value={voice.id} sx={{ fontSize: "13px" }}>
                    {voice.display_name}
                  </MenuItem>
                )) : null}
                {selectedAccent && voices[selectedAccent]?.Female?.length > 0 ? (
                  <ListSubheader sx={{ fontWeight: 'bold', color: '#1E4DE3' }}>Female Voices</ListSubheader>
                ) : null}
                {selectedAccent ? (voices[selectedAccent]?.Female || []).map(voice => (
                  <MenuItem key={voice.id} value={voice.id} sx={{ fontSize: "13px" }}>
                    {voice.display_name}
                  </MenuItem>
                )) : null}
              </Select>
            </FormControl>
            {audioVoice && audioVoice !== "Male" && audioVoice !== "Female" && selectedAccent && (
              <button
                onClick={(e) => {
                  let v = voices[selectedAccent]?.Male?.find(t => t.id === audioVoice);
                  if (!v) v = voices[selectedAccent]?.Female?.find(t => t.id === audioVoice);
                  if (v) handleTogglePlay(e, v);
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "4px",
                  border: "1px solid #1E4DE3", borderRadius: "15px", padding: "6px 12px",
                  fontSize: "12px", fontWeight: 500, cursor: "pointer",
                  backgroundColor: playingTrackId === audioVoice ? "#1E4DE3" : "transparent",
                  color: playingTrackId === audioVoice ? "#ffffff" : "#1E4DE3",
                  transition: "all 0.15s ease", height: "36px", flexShrink: 0
                }}
              >
                {playingTrackId === audioVoice ? (
                  <><PauseIcon style={{ fontSize: "14px" }} /><span>Pause</span></>
                ) : (
                  <><PlayIcon style={{ fontSize: "14px" }} /><span>Listen</span></>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="dialog-field" style={{ marginTop: "24px" }}>
          <FormLabel component="legend" className="dialog-label" focused={false} style={{ marginBottom: "12px" }}>
            Choose Audio Length
          </FormLabel>
          <div style={{ display: "flex", gap: "16px", width: "100%" }}>
            {audioDurationOptions.map((opt) => {
              const isSelected = audioDuration === opt.value;
              return (
                <div
                  key={opt.value}
                  onClick={() => setAudioDuration(opt.value)}
                  style={{
                    flex: 1,
                    padding: "16px",
                    border: isSelected ? "2px solid #1E4DE3" : "1px solid #e0e0e0",
                    borderRadius: "12px",
                    backgroundColor: isSelected ? "#f0f4ff" : "#fff",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    boxShadow: isSelected ? "0 4px 12px rgba(30, 77, 227, 0.15)" : "none",
                    transform: isSelected ? "translateY(-2px)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = "#b3c4f7";
                      e.currentTarget.style.backgroundColor = "#f8faff";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = "#e0e0e0";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  <span style={{
                    fontSize: "16px",
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? "#1E4DE3" : "#333",
                    marginBottom: "4px"
                  }}>
                    {opt.label}
                  </span>
                  <span style={{
                    fontSize: "13px",
                    color: isSelected ? "#1E4DE3" : "#666",
                    fontWeight: 400
                  }}>
                    {opt.desc}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{
          marginTop: "16px",
          fontSize: "14px",
          color: "#1E4DE3",
        }}>
          Credits required: <strong>2</strong>
        </div>
      </DialogContent>

      <DialogActions className="dialog-actions">
        <Button onClick={onClose} variant="text" className="cancel-btn">
          Cancel
        </Button>
        <Button
          onClick={onStartGeneratingAudio}
          variant="contained"
          className="start-generating-btn"
        >
          Start Generating
        </Button>
      </DialogActions>
    </Dialog>
  );
};
AudioSummaryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audioDuration: PropTypes.string.isRequired,
  setAudioDuration: PropTypes.func.isRequired,
  audioLanguage: PropTypes.string.isRequired,
  setAudioLanguage: PropTypes.func.isRequired,
  audioVoice: PropTypes.string.isRequired,
  setAudioVoice: PropTypes.func.isRequired,
  audioLevel: PropTypes.string.isRequired,
  setAudioLevel: PropTypes.func.isRequired,
  onStartGeneratingAudio: PropTypes.func.isRequired,
};

export default AudioSummaryDialog;
